import { actionHistory } from '../../store';
import { lockedDie } from '../../store';
import { DieFace, faces as faceList } from './face.type';

/**
 * Something that rolls a die: the board's <Die> component, which animates it,
 * or a stand-in that rolls it on the spot. Resolves false on a broken face.
 */
export interface DieRoller {
  roll(): Promise<boolean>;
  getDie(): Die;
}

export type DieCreation = {
  type: DieType;
  level: number;
  material: DieMaterial;
};

/** Where an invoked die goes. A battle sets it while it runs; see setInvokeTarget(). */
let invokeTarget: ((die: Die) => void) | undefined;

/**
 * Sends invoked dice to `target` -- the battle, which gives each an index of
 * its own and puts it in the tray. Returns a function that undoes it. Without
 * a battle, invoked dice go straight into `lockedDie`.
 */
export function setInvokeTarget(target: (die: Die) => void) {
  invokeTarget = target;
  return () => {
    if (invokeTarget === target) invokeTarget = undefined;
  };
}

export function invokeDie(type: Array<DieFace>) {
  var die = new InvokedDie(type, lockedDie.length + 1, 'invoke' + Math.random());
  die.material = DieMaterial.Invoked;
  if (invokeTarget) {
    invokeTarget(die);
  } else {
    lockedDie.push(die);
  }

  actionHistory.push(`invoked a '${type.join('+')}' die`);
}

export function createDie(dieIn: string | DieCreation, index?: number, id?: string) {
  if (typeof dieIn === 'string') {
    let creation: DieCreation = {
      type: dieIn.split('>')[0] as DieType,
      level: parseInt(dieIn.split('>')[1]) || 0,
      material: DieMaterial.Normal,
    };
    return new Die(creation, index, id);
  }
  return new Die(dieIn, index, id);
}

export class Die {
  id: string;
  type: DieType;
  colour: string;
  index: number;
  faces: Array<Array<DieFace>>;
  material: DieMaterial;

  used: boolean;
  // locked: boolean;
  selected: boolean;
  level: number;
  activeFace: number;

  constructor(dieIn: DieCreation, index: number = 0, id: string = 'x0') {
    this.type = dieIn.type;
    this.level = dieIn.level;
    this.material = dieIn.material;

    this.activeFace = 5;

    this.index = index;
    this.id = id;

    this.used = false;
    // this.locked = false;
    this.selected = false;

    if (!dieCollection[this.type]) {
      console.error(`No die of type: ${this.type}`);
      this.colour = 'white';
      this.faces = [];
      return;
    }

    this.colour = dieCollection[this.type].colour;
    this.faces = dieCollection[this.type].faces.slice(2 + this.level, 2 + this.level + 6);
  }

  clone() {
    var clone = new (Object.getPrototypeOf(this).constructor)(
      {
        type: this.type,
        level: this.level,
        material: this.material,
      },
      this.index,
      this.id,
    );

    clone.type = this.type;
    clone.colour = this.colour;
    clone.index = this.index;
    clone.faces = this.faces;

    clone.material = this.material;

    clone.activeFace = this.activeFace;
    clone.used = this.used;
    // clone.locked = this.locked;
    clone.selected = this.selected;
    clone.id = this.id; //Math.random();

    return clone;
  }

  value(): Array<DieFace> {
    return this.faces[this.activeFace];
  }

  /** Lands on a random face and returns it. */
  roll(): Array<DieFace> {
    this.activeFace = Math.floor(Math.random() * 6);
    return this.value();
  }

  get label() {
    return `${this.type} ${this.level}`;
  }

  setLevel(levelIn: number) {
    this.level = Math.min(Math.max(levelIn, -2), 2);
    this.faces = dieCollection[this.type].faces.slice(2 + this.level, 2 + this.level + 6);
  }
}

export class InvokedDie extends Die {
  constructor(dieIn: Array<DieFace>, index?: number, id?: string) {
    super(
      {
        type: DieType.Blank,
        level: 0,
        material: DieMaterial.Invoked,
      },
      index,
      id,
    );

    this.colour = 'white';
    this.faces = new Array(6).fill(dieIn);
  }
}

export enum DieColour {
  Red = 'red',
  LightBlue = 'light-blue',
  DarkBlue = 'dark-blue',
  Green = 'green',
  Yellow = 'yellow',
  Purple = 'purple',
  Cyan = 'cyan',
  Pink = 'pink',
  White = 'white',
  Orange = 'orange',
}

export enum DieMaterial {
  Normal = 'normal',
  Stone = 'stone',
  Bone = 'bone',
  Glass = 'glass',
  Steel = 'steel',
  Shadow = 'shadow',
  Ethereal = 'ethereal',
  Invoked = 'invoked',
  Cloned = 'cloned',
  // Blood = 'blood',
  // Weighted = 'weighted',
}

export enum DieType {
  Longsword = 'longsword',
  Shortsword = 'shortsword',
  Shield = 'shield',
  Bracer = 'bracer',
  Helmet = 'helmet',
  Dagger = 'dagger',
  Cloak = 'cloak',
  Flask = 'flask',
  Shivs = 'shivs',
  Javellin = 'javellin',
  Axe = 'axe',
  Hammer = 'hammer',
  Club = 'club',
  Hoof = 'hoof',
  Horn = 'horn',
  Wand = 'wand',
  Staff = 'staff',
  Tome = 'tome',
  Crystal = 'crystal',
  Fire = 'fire',
  Lightning = 'lightning',
  Ice = 'ice',
  Earth = 'earth',
  Circlet = 'circlet',
  Amulet = 'amulet',
  Fangs = 'fangs',
  Claws = 'claws',
  Bite = 'bite',
  Paw = 'paw',
  Wings = 'wings',
  Stinger = 'stinger',
  Poison = 'poison',
  Web = 'web',
  Shell = 'shell',
  Pincer = 'pincer',
  Beak = 'beak',
  Bow = 'bow',
  Crossbow = 'crossbow',
  Blowgun = 'blowgun',
  Quiver = 'quiver',
  AmmoBag = 'ammo bag',
  Bandolier = 'bandolier',
  Case = 'case',
  Blank = 'blank',
  Test = 'test',
  Pipes = 'pipes',
  Spear = 'spear',
  Net = 'net',
  LuckyCoin = 'lucky coin',
  Mana = 'mana',
  Arcana = 'arcana',
  Intelligence = 'intelligence',
  Reflex = 'reflex',
  Speed = 'speed',
  Strength = 'strength',
  Resilience = 'resilience',
  Blood = 'blood',
  Stealth = 'stealth',
  Talented = 'talented',
  Rhythmic = 'rhythmic',
  Tentacle = 'tentacle',
  Tail = 'tail',
  Maw = 'maw',
}

export type DieDefinition = {
  colour: DieColour;
  faces: Array<Array<DieFace>>;
};

interface deff {
  [key: string]: DieDefinition;
}

export const dieCollection: deff = {
  [DieType.Longsword]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Sword, DieFace.Sword],
      [DieFace.Sword, DieFace.Sword],
      [DieFace.Sword],
      [DieFace.Sword],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Shortsword]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Sword, DieFace.Sword],
      [DieFace.Sword, DieFace.Agility],
      [DieFace.Sword],
      [DieFace.Sword],
      [DieFace.Agility],
      [DieFace.Agility],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Shield]: {
    colour: DieColour.DarkBlue,
    faces: [
      [DieFace.Shield, DieFace.Shield],
      [DieFace.Bone, DieFace.Bone],
      [DieFace.Shield],
      [DieFace.Shield],
      [DieFace.Bone],
      [DieFace.Bone],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Bracer]: {
    colour: DieColour.LightBlue,
    faces: [
      [DieFace.Shield, DieFace.Shield],
      [DieFace.Fist, DieFace.Fist],
      [DieFace.Shield],
      [DieFace.Shield],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Helmet]: {
    colour: DieColour.Yellow,
    faces: [
      [DieFace.Wildcard],
      [DieFace.Wildcard],
      [DieFace.Shield],
      [DieFace.Shield],
      [DieFace.Brain],
      [DieFace.Brain],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Dagger]: {
    colour: DieColour.Yellow,
    faces: [
      [DieFace.Dagger, DieFace.Dagger],
      [DieFace.Dagger, DieFace.Dagger],
      [DieFace.Dagger],
      [DieFace.Dagger],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Cloak]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Cloak, DieFace.Cloak],
      [DieFace.Cloak, DieFace.Cloak],
      [DieFace.Cloak],
      [DieFace.Cloak],
      [DieFace.Agility],
      [DieFace.Agility],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Flask]: {
    colour: DieColour.Pink,
    faces: [
      [DieFace.Poison, DieFace.OR, DieFace.Blood],
      [DieFace.Poison, DieFace.OR, DieFace.Blood],
      [DieFace.Poison],
      [DieFace.Poison],
      [DieFace.Blood],
      [DieFace.Blood],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Shivs]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Shiv, DieFace.Shiv],
      [DieFace.Shiv, DieFace.Shiv],
      [DieFace.Shiv],
      [DieFace.Shiv],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Javellin]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Spear, DieFace.Fist],
      [DieFace.Spear, DieFace.Fist],
      [DieFace.Spear],
      [DieFace.Spear],
      [DieFace.Dart],
      [DieFace.Dart],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Axe]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Axe, DieFace.Axe],
      [DieFace.Axe, DieFace.Axe],
      [DieFace.Axe],
      [DieFace.Axe],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Hammer]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Hammer, DieFace.Hammer],
      [DieFace.Hammer, DieFace.Fist],
      [DieFace.Hammer],
      [DieFace.Hammer],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Club]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Hammer, DieFace.Hammer],
      [DieFace.Hammer, DieFace.Agility],
      [DieFace.Hammer],
      [DieFace.Hammer],
      [DieFace.Agility],
      [DieFace.Agility],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Hoof]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Hoof, DieFace.Hoof],
      [DieFace.Hoof, DieFace.Hoof],
      [DieFace.Hoof],
      [DieFace.Hoof],
      [DieFace.Agility],
      [DieFace.Agility],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Horn]: {
    colour: DieColour.LightBlue,
    faces: [
      [DieFace.Horn, DieFace.Horn],
      [DieFace.Horn, DieFace.Horn],
      [DieFace.Horn],
      [DieFace.Horn],
      [DieFace.Bone],
      [DieFace.Bone],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Wand]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Fist, DieFace.Fire],
      [DieFace.Fist, DieFace.OR, DieFace.Fire],
      [DieFace.Fire],
      [DieFace.Fire],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Staff]: {
    colour: DieColour.Yellow,
    faces: [
      [DieFace.Reflex, DieFace.Lightning],
      [DieFace.Reflex, DieFace.OR, DieFace.Lightning],
      [DieFace.Lightning],
      [DieFace.Lightning],
      [DieFace.Reflex],
      [DieFace.Reflex],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Tome]: {
    colour: DieColour.Cyan,
    faces: [
      [DieFace.Brain, DieFace.Ice],
      [DieFace.Brain, DieFace.OR, DieFace.Ice],
      [DieFace.Ice],
      [DieFace.Ice],
      [DieFace.Brain],
      [DieFace.Brain],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Crystal]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Bone, DieFace.Earth],
      [DieFace.Bone, DieFace.OR, DieFace.Earth],
      [DieFace.Earth],
      [DieFace.Earth],
      [DieFace.Bone],
      [DieFace.Bone],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Fire]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Mana, DieFace.Fire],
      [DieFace.Mana, DieFace.OR, DieFace.Fire],
      [DieFace.Fire],
      [DieFace.Fire],
      [DieFace.Mana],
      [DieFace.Mana],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Lightning]: {
    colour: DieColour.Yellow,
    faces: [
      [DieFace.Mana, DieFace.Lightning],
      [DieFace.Mana, DieFace.OR, DieFace.Lightning],
      [DieFace.Lightning],
      [DieFace.Lightning],
      [DieFace.Mana],
      [DieFace.Mana],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Ice]: {
    colour: DieColour.Cyan,
    faces: [
      [DieFace.Mana, DieFace.Ice],
      [DieFace.Mana, DieFace.OR, DieFace.Ice],
      [DieFace.Ice],
      [DieFace.Ice],
      [DieFace.Mana],
      [DieFace.Mana],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Earth]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Mana, DieFace.Earth],
      [DieFace.Mana, DieFace.OR, DieFace.Earth],
      [DieFace.Earth],
      [DieFace.Earth],
      [DieFace.Mana],
      [DieFace.Mana],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Circlet]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Wildcard],
      [DieFace.Wildcard],
      [DieFace.Mana],
      [DieFace.Mana],
      [DieFace.Brain],
      [DieFace.Brain],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Amulet]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Fire, DieFace.OR, DieFace.Ice],
      [DieFace.Earth, DieFace.OR, DieFace.Lightning],
      [DieFace.Fire],
      [DieFace.Ice],
      [DieFace.Lightning],
      [DieFace.Earth],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Fangs]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Bite, DieFace.Blood],
      [DieFace.Bite, DieFace.OR, DieFace.Blood],
      [DieFace.Bite],
      [DieFace.Bite],
      [DieFace.Blood],
      [DieFace.Blood],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Claws]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Claws, DieFace.Claws],
      [DieFace.Claws, DieFace.Claws],
      [DieFace.Claws],
      [DieFace.Claws],
      [DieFace.Blood],
      [DieFace.Blood],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Bite]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Bite, DieFace.Bite],
      [DieFace.Bite, DieFace.Bite],
      [DieFace.Bite],
      [DieFace.Bite],
      [DieFace.Note],
      [DieFace.Note],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Paw]: {
    colour: DieColour.Yellow,
    faces: [
      [DieFace.Claws, DieFace.Claws],
      [DieFace.Claws, DieFace.Claws],
      [DieFace.Claws],
      [DieFace.Claws],
      [DieFace.Agility],
      [DieFace.Agility],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Maw]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Bite, DieFace.Stinger],
      [DieFace.Bite, DieFace.Stinger],
      [DieFace.Bite],
      [DieFace.Bite],
      [DieFace.Stinger],
      [DieFace.Stinger],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Wings]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Wings, DieFace.Wings],
      [DieFace.Wings, DieFace.Wings],
      [DieFace.Wings],
      [DieFace.Wings],
      [DieFace.Agility],
      [DieFace.Agility],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Stinger]: {
    colour: DieColour.Yellow,
    faces: [
      [DieFace.Stinger, DieFace.Stinger],
      [DieFace.Stinger, DieFace.Stinger],
      [DieFace.Stinger],
      [DieFace.Stinger],
      [DieFace.Poison],
      [DieFace.Poison],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Poison]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Poison, DieFace.Poison],
      [DieFace.Poison, DieFace.Poison],
      [DieFace.Poison],
      [DieFace.Poison],
      [DieFace.Poison],
      [DieFace.Poison],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Web]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Web, DieFace.Web],
      [DieFace.Web, DieFace.Web],
      [DieFace.Web],
      [DieFace.Web],
      [DieFace.Agility],
      [DieFace.Agility],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Shell]: {
    colour: DieColour.LightBlue,
    faces: [
      [DieFace.Shell, DieFace.Shell],
      [DieFace.Shell, DieFace.Shell],
      [DieFace.Shell],
      [DieFace.Shell],
      [DieFace.Bone],
      [DieFace.Bone],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Pincer]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Pincer, DieFace.Pincer],
      [DieFace.Pincer, DieFace.Pincer],
      [DieFace.Pincer],
      [DieFace.Pincer],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Beak]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Beak, DieFace.Beak],
      [DieFace.Beak, DieFace.Note],
      [DieFace.Beak],
      [DieFace.Beak],
      [DieFace.Note],
      [DieFace.Note],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Bow]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Bow, DieFace.Bow],
      [DieFace.Bow, DieFace.Ammo],
      [DieFace.Bow],
      [DieFace.Bow],
      [DieFace.Bow],
      [DieFace.Ammo],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Crossbow]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Crossbow, DieFace.Crossbow],
      [DieFace.Crossbow, DieFace.Shield],
      [DieFace.Crossbow],
      [DieFace.Crossbow],
      [DieFace.Shield],
      [DieFace.Shield],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Blowgun]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Blowgun, DieFace.Blowgun],
      [DieFace.Blowgun, DieFace.Poison],
      [DieFace.Blowgun],
      [DieFace.Blowgun],
      [DieFace.Poison],
      [DieFace.Poison],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Quiver]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Ammo, DieFace.Ammo],
      [DieFace.Ammo, DieFace.Ammo],
      [DieFace.Ammo],
      [DieFace.Ammo],
      [DieFace.Eye],
      [DieFace.Eye],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.AmmoBag]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Ammo, DieFace.Poison],
      [DieFace.Ammo, DieFace.Blood],
      [DieFace.Ammo, DieFace.OR, DieFace.Poison],
      [DieFace.Ammo, DieFace.OR, DieFace.Blood],
      [DieFace.Ammo],
      [DieFace.Ammo],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Bandolier]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Ammo, DieFace.Eye],
      [DieFace.Ammo, DieFace.Eye],
      [DieFace.Dart],
      [DieFace.Dart],
      [DieFace.Ammo],
      [DieFace.Ammo],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Case]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Ammo, DieFace.Fist],
      [DieFace.Ammo, DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Ammo],
      [DieFace.Ammo],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Blank]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Blank],
    ],
  },

  [DieType.Pipes]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Blowgun, DieFace.Blowgun],
      [DieFace.Blowgun, DieFace.Note],
      [DieFace.Blowgun],
      [DieFace.Blowgun],
      [DieFace.Note],
      [DieFace.Note],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Spear]: {
    colour: DieColour.Orange,
    faces: [
      [DieFace.Spear, DieFace.Spear],
      [DieFace.Spear, DieFace.Spear],
      [DieFace.Spear],
      [DieFace.Spear],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Net]: {
    colour: DieColour.Yellow,
    faces: [
      [DieFace.Web, DieFace.Web],
      [DieFace.Web, DieFace.OR, DieFace.Fist],
      [DieFace.Web],
      [DieFace.Web],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.LuckyCoin]: {
    colour: DieColour.Yellow,
    faces: [
      [DieFace.Wildcard],
      [DieFace.Wildcard],
      [DieFace.Wildcard],
      [DieFace.Broken],
      [DieFace.Broken],
      [DieFace.Broken],
      [DieFace.Broken],
      [DieFace.Broken],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },

  [DieType.Mana]: {
    colour: DieColour.LightBlue,
    faces: [
      [DieFace.Mana, DieFace.Mana],
      [DieFace.Mana, DieFace.Mana],
      [DieFace.Mana],
      [DieFace.Mana],
      [DieFace.Mana],
      [DieFace.Mana],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Arcana]: {
    colour: DieColour.LightBlue,
    faces: [
      [DieFace.Mana, DieFace.Brain],
      [DieFace.Mana, DieFace.Brain],
      [DieFace.Mana],
      [DieFace.Mana],
      [DieFace.Brain],
      [DieFace.Brain],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Intelligence]: {
    colour: DieColour.LightBlue,
    faces: [
      [DieFace.Wildcard],
      [DieFace.Wildcard],
      [DieFace.Brain],
      [DieFace.Brain],
      [DieFace.Bone],
      [DieFace.Bone],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Reflex]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Eye, DieFace.Reflex],
      [DieFace.Eye, DieFace.OR, DieFace.Reflex],
      [DieFace.Eye],
      [DieFace.Eye],
      [DieFace.Reflex],
      [DieFace.Reflex],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Speed]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Agility, DieFace.Agility],
      [DieFace.Reflex, DieFace.Reflex],
      [DieFace.Agility],
      [DieFace.Agility],
      [DieFace.Reflex],
      [DieFace.Reflex],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Strength]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Fist, DieFace.Bone],
      [DieFace.Fist, DieFace.OR, DieFace.Bone],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Bone],
      [DieFace.Bone],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Resilience]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Bone, DieFace.Blood],
      [DieFace.Bone, DieFace.OR, DieFace.Blood],
      [DieFace.Bone],
      [DieFace.Bone],
      [DieFace.Blood],
      [DieFace.Blood],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Blood]: {
    colour: DieColour.Red,
    faces: [
      [DieFace.Blood, DieFace.Blood],
      [DieFace.Blood, DieFace.Blood],
      [DieFace.Blood],
      [DieFace.Blood],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Stealth]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Cloak, DieFace.Eye],
      [DieFace.Cloak, DieFace.OR, DieFace.Eye],
      [DieFace.Cloak],
      [DieFace.Cloak],
      [DieFace.Eye],
      [DieFace.Eye],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Talented]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Eye, DieFace.OR, DieFace.Agility],
      [DieFace.Bone, DieFace.OR, DieFace.Fist],
      [DieFace.Eye],
      [DieFace.Agility],
      [DieFace.Bone],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Rhythmic]: {
    colour: DieColour.Green,
    faces: [
      [DieFace.Note, DieFace.Note],
      [DieFace.Note, DieFace.Note],
      [DieFace.Note],
      [DieFace.Note],
      [DieFace.Reflex],
      [DieFace.Reflex],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Tentacle]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Prehensile, DieFace.Prehensile],
      [DieFace.Prehensile, DieFace.Fist],
      [DieFace.Prehensile],
      [DieFace.Prehensile],
      [DieFace.Fist],
      [DieFace.Fist],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
  [DieType.Tail]: {
    colour: DieColour.Purple,
    faces: [
      [DieFace.Prehensile, DieFace.Prehensile],
      [DieFace.Prehensile, DieFace.Reflex],
      [DieFace.Prehensile],
      [DieFace.Prehensile],
      [DieFace.Reflex],
      [DieFace.Reflex],
      [DieFace.Blank],
      [DieFace.Blank],
      [DieFace.Broken],
      [DieFace.Broken],
    ],
  },
};

const parseDice = () => {
  Object.keys(dieCollection).forEach((key) => {
    dieCollection[key].faces.reverse();
  });
};
parseDice();
